/**
 * The machine's own settings, drawn read-only.
 *
 * `settings.tsx` in the Electron tree was 949 lines and it *wrote* the hardware
 * configuration from the page. Here `kiosk.toml` is the durable answer and the
 * one write is `shell_select_printer`, which takes an index into a scan the
 * shell performed — never an address. A value the page can supply is a value
 * anyone who compromised the deployment can supply.
 *
 * What the screen adds beyond the file is the buttons, because a test print is
 * the only thing a config file cannot be.
 */
import { useEffect, useState } from 'react'
import { Button, Card, Notice, Spinner } from '@/components/ui'
import { useT } from '@/i18n'
import {
  forgetShellInfo,
  inShell,
  listDevices,
  listen,
  probeTerminal,
  printerStatus,
  selectPrinter,
  shellInfo
} from '@/shell/native'
import type { Device, PrinterStatus, ShellInfo } from '@/shell/native'
import { useSession } from '@/store/session'
import { runTestPrint } from '@/services/test-print'

export function Settings({ onClose }: { onClose: () => void }) {
  const t = useT()
  const [info, setInfo] = useState<ShellInfo | undefined>()
  const [loaded, setLoaded] = useState(false)
  const [devices, setDevices] = useState<Device[] | null>(null)
  const [status, setStatus] = useState<PrinterStatus | undefined>()
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const session = useSession()

  const refresh = async () => {
    setInfo(await shellInfo())
    setStatus(await printerStatus())
    setLoaded(true)
  }

  useEffect(() => {
    void refresh()
  }, [])

  // The lock is the one thing on this screen that something else can change
  // while it is open: the shell's system menu locks the window, and its PIN pad
  // unlocks it. Without this the screen keeps saying «cannot be closed from the
  // screen» about a window that now has a close box on it.
  useEffect(() => {
    const stops: Array<() => void> = []
    // The lock, the terminal list and the printer are all changed from the
    // shell's own windows — the system menu locks, its PIN pad unlocks, its
    // setup window adds a terminal and chooses a printer — and none of that
    // goes through this page. Without these the screen keeps saying «cannot be
    // closed from the screen» about a window that now has a close box, keeps
    // listing the terminals the process started with, and names a printer the
    // machine has stopped using.
    for (const event of ['shell://kiosk-changed', 'shell://terminals-changed', 'shell://printer-changed'] as const) {
      void listen(event, () => {
        forgetShellInfo()
        void refresh()
      }).then((unlisten) => stops.push(unlisten))
    }
    return () => {
      for (const stop of stops) stop()
    }
  }, [])

  const scan = async () => {
    setBusy(true)
    try {
      setDevices(await listDevices(true))
    } catch (error) {
      setMessage(String(error))
    } finally {
      setBusy(false)
    }
  }

  const testPrint = async () => {
    setBusy(true)
    // `services/test-print.ts`, so the console's remote request and this button
    // print exactly the same slip. Two different test pages is a support call
    // that learns nothing.
    setMessage((await runTestPrint()).summary)
    setBusy(false)
  }

  if (!loaded) return <Spinner />

  return (
    <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-5 overflow-y-auto p-6">
      <header className="flex items-center justify-between">
        <h1 className="text-[length:var(--text-kiosk-xl)] font-bold">{t('settings.title')}</h1>
        <Button variant="ghost" onClick={onClose}>
          {t('settings.close')}
        </Button>
      </header>

      {!inShell() && <Notice tone="warn">{t('settings.noShell')}</Notice>}

      {info !== undefined && info.problems.length > 0 && (
        <Notice tone="bad">
          <strong>{t('settings.problems')}</strong>
          <ul className="mt-2 list-disc pl-5">
            {info.problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </Notice>
      )}

      <Card className="space-y-2 p-5">
        <h2 className="font-bold">{t('settings.device')}</h2>
        <Row label="Branch" value={session.branch?.name ?? '—'} />
        <Row label="Device" value={session.deviceName ?? '—'} />
        {/* Set from the console, never here: it decides what 1C is told and whether cash is real. */}
        <Row label="Till" value={session.branch?.mode === 'pos' ? 'POS (staffed)' : 'Kiosk (self-service)'} />
        {/*
          A token in localStorage is a different security posture than one in
          the keychain, and somebody should be able to see which they have.
        */}
        <Row label="Token" value={session.tokenSource} />
        <Row label="Socket" value={session.socketConnected ? 'connected' : 'offline'} />
        <Row label="Outbox" value={String(session.outbox.length)} />
        {info !== undefined && (
          <>
            <Row label="Shell" value={`${info.version} · ${info.platform}/${info.arch}`} />
            {/*
              Whether this machine can be closed, and by whom. `exitLocked`
              false while `kiosk` is true is the state worth seeing: the window
              is locked and no PIN was configured, so nothing on the screen
              closes it. The digest never crosses the wire, so there is nothing
              to read off this row.
            */}
            <Row
              label="Lock"
              value={
                !info.kiosk
                  ? 'windowed'
                  : info.exitLocked
                    ? 'kiosk · a PIN closes it'
                    : 'kiosk · no exit PIN, cannot be closed here'
              }
            />
            <Row label="Config" value={`${info.configPath ?? '—'} (${info.configSource})`} />
            <Row label="Log" value={info.logPath ?? '—'} />
          </>
        )}
      </Card>

      <Card className="space-y-3 p-5">
        <h2 className="font-bold">{t('settings.printer')}</h2>
        {info === undefined ? (
          <p className="text-[var(--color-ink-soft)]">{t('settings.noShell')}</p>
        ) : (
          <>
            <Row label="Target" value={info.printerTarget ?? '—'} />
            <Row label="Kind" value={info.printerKind} />
            {/*
              `MGC_PRINTER` wins for the run. A selection made below is written
              to `kiosk.toml` and takes effect at the next restart, so without
              this row the button looks like it did nothing.
            */}
            {info.printerFromEnv && (
              <Notice tone="warn">MGC_PRINTER is set and overrides this until the shell restarts.</Notice>
            )}
            <Row label="Mode" value={`${info.receipt.mode} · ${info.receipt.dots} dots`} />
            {/*
              Every field of the status is nullable because "the printer did not
              say" stays distinct from "no" all the way to here. Plenty of cheap
              units implement printing and not status.
            */}
            <Row
              label="Status"
              value={
                status === undefined || status.unavailable
                  ? 'unavailable on this connection'
                  : [
                      status.online === null ? null : status.online ? 'online' : 'offline',
                      status.paperOut === true ? 'paper out' : null,
                      status.coverOpen === true ? 'cover open' : null
                    ]
                      .filter(Boolean)
                      .join(', ') || 'no answer'
              }
            />
          </>
        )}

        <div className="flex flex-wrap gap-3 pt-2">
          <Button variant="ghost" disabled={!inShell() || busy} onClick={() => void scan()}>
            {t('settings.scan')}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => void testPrint()}>
            {t('settings.testPrint')}
          </Button>
          <Button variant="ghost" disabled={!inShell() || busy} onClick={() => void refresh()}>
            {t('common.retry')}
          </Button>
        </div>

        {devices !== null && (
          <ul className="divide-y divide-[var(--color-line)] pt-3">
            {devices.map((device) => (
              <li key={device.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="font-medium">{device.label}</p>
                  <p className="text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-soft)]">
                    {device.source} · {device.wire}
                    {device.detail !== null && ` · ${device.detail}`}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  // A printer-class device no queue claims is a driver nobody
                  // installed. There is nothing to print to yet, and the row
                  // says so rather than offering a button that cannot work.
                  disabled={!device.selectable || busy}
                  onClick={async () => {
                    await selectPrinter(device.id)
                    await refresh()
                  }}
                >
                  {t('common.confirm')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="space-y-3 p-5">
        <h2 className="font-bold">{t('settings.terminal')}</h2>
        <Row label="Configured" value={info?.terminals.join(', ') || 'none'} />
        <Button
          variant="ghost"
          disabled={!inShell() || (info?.terminals ?? []).length === 0 || busy}
          onClick={async () => {
            setBusy(true)
            // The first configured terminal; a shop with two picks per payment.
            const result = await probeTerminal(info?.terminals[0] ?? '').catch((error: unknown) => ({
              // A probe that threw moved no money, which is `declined` and not
              // `unknown` — the request never reached a card.
              outcome: 'declined' as const,
              message: String(error),
              approvalCode: null,
              maskedPan: null,
              code: null
            }))
            setMessage(result.message)
            setBusy(false)
          }}
        >
          {t('settings.testTerminal')}
        </Button>
      </Card>

      {message !== null && <Notice tone="good">{message}</Notice>}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <p className="flex justify-between gap-4 border-b border-[var(--color-line)] py-2 last:border-0">
      <span className="text-[var(--color-ink-soft)]">{label}</span>
      <span className="text-right font-medium break-all">{value}</span>
    </p>
  )
}
